import React, { useState } from "react";
import { ReactComponent as BookmarkIcon } from "assets/icons/bookmark.svg";
import { ReactComponent as BedroomIcon } from "assets/icons/bedroom_filled.svg";
import { ReactComponent as BathIcon } from "assets/icons/bath_filled.svg";
import { ReactComponent as LocationIcon } from "assets/icons/location_filled.svg";
import { ReactComponent as ExternalLinkIcon } from "assets/icons/external_link.svg";
import { ReactComponent as PropertySquireIcon } from "assets/icons/property_squire.svg";
import { ReactComponent as Info } from "assets/icons/info_with_bg.svg";
import { formatCurrency } from "utils/formatNumber";
import {
  formatExternalPropertyLocation,
  formatPropertyLocation,
} from "utils/formatText";
import { ReactComponent as EditIcon } from "assets/icons/edit_pencil.svg";
import useBookmark from "hooks/useBookmark";
import useProperties from "hooks/useProperties";

const ExternalPropertyCard = ({
  externalProperty,
  setIsRemovePropertyPopupOpen,
  setPropertyToRemove,
  setOpenEditExternalPropertyNotePopup,
}) => {
  const { propertyId, propertyType } = externalProperty;
  const { togglePropertyNoteDialog } = useBookmark();

  // Determine if the property is sourced externally or already exists in our database.
  // "Property" means it was added from an external URL but is already in our database.
  // "ExternalProperty" means it's not found in our DB and has been scraped from an external source.
  const isExternal = propertyType === "ExternalProperty";

  // Remove external property
  const handleRemoveClick = () => {
    setPropertyToRemove(propertyId);
    setIsRemovePropertyPopupOpen(true);
  };

  // Return if the property id is not populate
  if (!propertyId) return;

  return (
    <>
      <div
        className={`${
          isExternal ? "border-[#D9D9D9]" : "border-primary"
        } w-full xs:w-[370px] h-[512px] border rounded-[10px] p-[10px] flex flex-col`}
      >
        <div className="h-[260px] relative">
          {isExternal ? (
            propertyId?.imageUrl !== null ? (
              <img
                src={propertyId.imageUrl}
                alt="property"
                className="w-full object-cover h-full"
              />
            ) : (
              <div className="w-full h-full bg-[#ebebeb]" />
            )
          ) : (
            <img
              src={propertyId.pictures[0]}
              alt="property"
              className="w-full object-cover h-full"
            />
          )}

          {!isExternal && (
            <div
              data-tooltip-id="property_info_tooltip"
              data-tooltip-content="Questa proprietà è verificata da HouseScan e dispone di informazioni avanzate"
              className="absolute top-[18px] right-[12px] cursor-pointer"
            >
              <Info />
            </div>
          )}

          <div
            onClick={() => {
              setOpenEditExternalPropertyNotePopup(externalProperty);
              togglePropertyNoteDialog(externalProperty?._id);
            }}
            className="w-6 h-6 absolute right-[7px] z-[1] top-[7px] rounded-[5px] bg-white cursor-pointer note-btn-shadow flex items-center justify-center"
          >
            <EditIcon
              className={`${
                externalProperty?.note ? "text-primary" : "text-[#0000004D]"
              }`}
            />
          </div>

          {propertyId?.logo && (
            <div className="absolute top-[11px] left-[9px] h-[46px] w-[182px] rounded-[5px] bg-[#FBFCFD] p-2 flex items-center justify-center">
              <img src={propertyId?.logo} alt="brand_logo" className="w-full" />
            </div>
          )}
          <div className="bg-white absolute right-[5px] -bottom-[28.5px] rounded-[11px] border border-[#F0F0F0] w-[77px] h-[85px] flex items-center justify-center">
            <BookmarkIcon
              onClick={handleRemoveClick}
              className="cursor-pointer text-black"
            />
          </div>
        </div>
        <div className="mt-[30px] flex-grow flex flex-col">
          <h1 className="text-black font-semibold text-[20px] leading-[20px] px-[10px] line-clamp-2">
            {propertyId?.title}
          </h1>
          {propertyId?.location && (
            <div className="flex items-start space-x-1 mt-[16px] px-[10px]">
              <LocationIcon />
              <p className="text-[#777E90] text-[16px] font-normal leading-[20px] truncate">
                {isExternal
                  ? formatExternalPropertyLocation(propertyId?.location)
                  : formatPropertyLocation(propertyId)}
              </p>
            </div>
          )}
          <div className="flex flex-grow items-center space-x-[11px] mt-[16px] px-[10px]">
            {propertyId.bedrooms && (
              <div className="flex items-center space-x-[5px]">
                <BedroomIcon className="text-black" />
                <p className="text-[#777E90] text-[16px] leading-5 font-medium">
                  {isExternal
                    ? propertyId?.bedrooms
                    : `${propertyId?.bedrooms} locali`}
                </p>
              </div>
            )}
            {propertyId?.bathrooms && (
              <div className="flex items-center space-x-[5px]">
                <BathIcon className="fill-black stroke-none" strokeWidth={0} />
                <p className="text-[#777E90] text-[16px] leading-5 font-medium">
                  {isExternal
                    ? propertyId?.bathrooms
                    : `${propertyId?.bathrooms} Bagno`}
                </p>
              </div>
            )}
            {isExternal ? (
              propertyId?.squareFootage && (
                <div className="flex items-center space-x-[5px]">
                  <PropertySquireIcon className="text-black" />
                  <p className="text-[#777E90] text-[16px] leading-5 font-medium">
                    {propertyId?.squareFootage}
                  </p>
                </div>
              )
            ) : (
              <div className="flex items-center space-x-[5px]">
                <PropertySquireIcon className="text-black" />
                <p className="text-[#777E90] text-[16px] leading-5 font-medium">
                  {propertyId?.total_area} m²
                </p>
              </div>
            )}
          </div>
          <hr className="h-[1px] bg-[#D9D9D9] my-5" />
          <div className="flex items-center justify-between pl-[10px] pr-[19px]">
            <h4 className="text-[#0F0F0F] pb-[6px] font-semibold leading-[20px] text-[20px] tracking-tight">
              {isExternal
                ? propertyId?.price
                : formatCurrency(
                    propertyId.sale_price,
                    propertyId?.sale_currency
                  )}
            </h4>
            <div
              onClick={() =>
                isExternal && window.open(propertyId?.url, "_blank")
              }
              className={`${
                isExternal ? "border-[#D9D9D9]" : "border-primary"
              } hover:bg-primary hover:stroke-white stroke-black transition-all duration-300 ease-in-out text-white hover:text-white cursor-pointer h-[46px] px-[46px] border flex items-center justify-center rounded-[30px]`}
            >
              <ExternalLinkIcon />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ExternalPropertyCard;
